import { Fragment } from 'react'

import { VISIT_URL } from 'consts'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useTheme } from 'styled-components'

import BannerMSJ from 'components/BannerMSJ'
import { getCookieConsent, useCookieConsents } from 'components/GDPRPopup/useCookiePreferences'
import { NocookieConsentPlaceholder } from 'components/NocookieConsentPlaceholder'
import {
  PartAdvancedModule,
  PartAudio,
  PartButton,
  PartImageAnd3D,
  PartKeyCode,
  PartText,
  PartVideo,
} from './Parts'
import { StyledTopImage } from './styles'

const PartIntegrations = dynamic(import('./Parts/PartIntegrations'))
const PartPremium = dynamic(import('./Parts/PartPremium'), { ssr: false })
const CardHeader = dynamic(import('components/CardHeader'))
const AlatableAirCanada = dynamic(import('components/AlatableAirCanada'))
const SurveyMonkey = dynamic(import('components/SurveyMonkey'))
const FBSContent = dynamic(import('components/FBSContent'))

const CardDisplay = ({ cardInfos, appInfos, themeInfos, visitorId, fbsInfos, isPwa = false }) => {
  const theme = useTheme()
  const cookiePreferences = useCookieConsents();
  const hasConsent = (type) =>
  getCookieConsent(cookiePreferences, type);

  const getPartDisplay = (part, i) => {
    if (!hasConsent(part.type)) {
      return <NocookieConsentPlaceholder mediaType={part.type} />
    }
    switch (part.type) {
      case 'H2':
      case 'Text':
      case 'Quote':
      case 'Separator':
      case 'Framed':
        return <PartText part={part} themeInfos={themeInfos} />
      case 'Picture':
      case 'PictureUrl':
      case 'SketchFab':
      case 'Giphy':
      case 'BeforeAfter':
      case 'Panorama360':
      case 'SlideShow':
        return <PartImageAnd3D part={part} themeInfos={themeInfos} />
      case 'Audio':
      case 'SoundCloud':
        return <PartAudio part={part} partIndex={i} />
      case 'VideoYoutube':
      case 'Video':
      case 'Vimeo':
        return <PartVideo part={part} />
      case 'Button':
      case 'ButtonFileUpload':
      case 'Button-Half':
      case 'FacebookShareButton':
      case 'BtnUnity':
        return (
          <PartButton
            part={part}
            appInfos={appInfos}
            cardInfos={cardInfos}
            currentIndex={i}
            fbsInfos={fbsInfos}
            themeInfos={themeInfos}
          />
        )
      case 'FormGoogle':
      case 'GoogleMaps':
        return <PartIntegrations part={part} />
      case 'Photobooth':
      case 'AugmentedReality':
      case 'EnhancedReality':
      case 'Voxpop':
      case 'List':
      case 'Puzzle':
      case 'Map':
        return (
          <PartPremium
            part={part}
            cardInfos={cardInfos}
            appInfos={appInfos}
            visitorId={visitorId}
            partIndex={i}
            themeInfos={themeInfos}
            isPwa={isPwa}
          />
        )
      case 'Lock':
      case 'LockCancel':
      case 'LockPage':
      case 'Dispatcher':
        return <PartKeyCode part={part} themeInfos={themeInfos} />
      case 'Riddle':
      case 'RiddleCancel':
      case 'RiddlePage':
        return <PartKeyCode part={part} themeInfos={themeInfos} />
      case 'VisitCounter':
      case 'Trigger':
      case 'Lotodemo':
      case 'Lotodemoembed':
      case 'Lotodemolegend':
      case 'Lotodemobabies':
      case 'Lotodemofreespins':
      case 'Lotodemoreelways':
      case 'ListItem':
      case 'Input':
        return (
          <PartAdvancedModule part={part} appInfos={appInfos} cardInfos={cardInfos} visitorId={visitorId} />
        )
      // not parts of a specific part in admin
      case 'fbs-content':
        if (fbsInfos && fbsInfos.file && fbsInfos.file.url) {
          return <FBSContent fbsInfos={fbsInfos} />
        }
        return
      case 'AlatableAirCanada':
        return <AlatableAirCanada part={part} />
      default:
        return <div />
    }
  }

  const url =
    cardInfos.tokenId === cardInfos.cardId
      ? `${VISIT_URL}/${appInfos.id}/${cardInfos.cardId}`
      : `${VISIT_URL}/${appInfos.id}/card/${cardInfos.cardId}`

  const ogImg = cardInfos.parts.find((part) => part.type === 'Picture')

  const title = `${cardInfos.title} - ${appInfos.name}`

  return (
    <>
      {cardInfos && appInfos.name && (
        <Helmet>
          <title>
            {title}
          </title>

          <meta property="og:title" content={`${cardInfos.title} - ${appInfos.name}`} />

          {cardInfos.type !== 'facebook-share-content' && <meta property="og:url" content={url} />}

          {cardInfos.type === 'facebook-share-content' && fbsInfos && fbsInfos.id && (
            <meta property="og:url" content={`${VISIT_URL}/${appInfos.id}/fbs/${fbsInfos.id}`} />
          )}

          <meta property="og:description" content={appInfos.name} />

          {ogImg && ogImg.url && <meta property="og:image" content={ogImg.url} />}

          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="627" />
        </Helmet>
      )}

      {cardInfos && (
        <>
          {themeInfos && <BannerMSJ themeInfos={themeInfos} />}

          {theme.card_header_image_top && (
            <StyledTopImage src={`/static/images/${theme.card_header_image_top}`} />
          )}

          <CardHeader cardInfos={cardInfos} appInfos={appInfos} themeInfos={themeInfos} />


          <SurveyMonkey appId={appInfos.id} tokenId={cardInfos.cardId} />

          {cardInfos.parts.map(
            (part, i) =>
              part.type && <Fragment key={`part-${part.type}-${i}`}>{getPartDisplay(part, i)}</Fragment>
          )}
        </>
      )}
    </>
  )
}

CardDisplay.propTypes = {
  cardInfos: PropTypes.object,
  appInfos: PropTypes.object,
  themeInfos: PropTypes.object,
  visitorId: PropTypes.any,
  fbsInfos: PropTypes.object,
}

export default CardDisplay
